import type { AccountRole, DisplayPreferenceType, UserProfile } from '../types/schema'

export type DisplayNameSource = {
  displayPreferences?: UserProfile['displayPreferences']
  firstName?: string | null
  lastName?: string | null
  username?: string | null
  phone?: string | null
  email?: string | null
  displayName?: string | null
  role?: UserProfile['role']
} | null | undefined

export const DEFAULT_ACCOUNT_ROLE: AccountRole = 'MEMBER'
export const DEFAULT_DISPLAY_PREFERENCE: DisplayPreferenceType = 'FIRST_LAST'
export const USERNAME_PATTERN = /^[a-z0-9](?:[a-z0-9-]{4,38}[a-z0-9])$/

const clean = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : ''

const firstLastName = (source: DisplayNameSource): string => {
  const firstName = clean(source?.firstName)
  const lastName = clean(source?.lastName)
  return [firstName, lastName].filter(Boolean).join(' ')
}

const fallbackName = (source: DisplayNameSource): string =>
  firstLastName(source)
  || clean(source?.displayName)
  || clean(source?.email)
  || clean(source?.phone)
  || 'Account'

export const computeDisplayName = (source: DisplayNameSource): string => {
  const preference = source?.displayPreferences?.type
  if (preference === 'USERNAME') return clean(source?.username) || fallbackName(source)
  if (preference === 'PHONE') return clean(source?.phone) || fallbackName(source)
  if (preference === 'FIRST_LAST') return firstLastName(source) || fallbackName(source)
  return fallbackName(source)
}

export const resolveComputedDisplayName = (source: DisplayNameSource): string =>
  clean(source?.displayPreferences?.computedName) || computeDisplayName(source)

export const normalizeAccountRole = (value: unknown): AccountRole =>
  value === 'HELPER' ? 'HELPER' : DEFAULT_ACCOUNT_ROLE

export const normalizeUsername = (value: string): string =>
  value.trim().toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40).replace(/-+$/g, '')

export const validateUsername = (value: string): string | null => {
  const trimmed = value.trim()
  if (!trimmed) return 'Enter a username.'
  if (trimmed !== value) return 'Username cannot start or end with spaces.'
  if (/[A-Z]/.test(trimmed)) return 'Use lowercase letters only. Uppercase is not allowed.'
  if (trimmed.length < 6 || trimmed.length > 40) return 'Username must be 6-40 characters.'
  if (!USERNAME_PATTERN.test(trimmed)) return 'Use lowercase letters, numbers, and hyphens. Start and end with a letter or number.'
  return null
}

export const helperUsernameFor = (internalUid: string): string => {
  const suffix = normalizeUsername(internalUid).replace(/-/g, '').slice(-10) || 'helper'
  return `helper-${suffix}`.slice(0, 40)
}

export const splitName = (displayName: string | null | undefined): { firstName: string; lastName: string } => {
  const parts = clean(displayName).split(/\s+/).filter(Boolean)
  if (parts.length === 0) return { firstName: '', lastName: '' }
  if (parts.length === 1) return { firstName: parts[0], lastName: '' }
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') }
}

export const isPhoneOnlyIdentity = (
  user: { phoneNumber?: string | null; email?: string | null } | null | undefined,
): boolean => Boolean(user && user.phoneNumber && !user.email)

